import { isValidSalesforceAccountId } from '@/features/SalesforceAccounts/lib/salesforceOutcome'
import { salesforceAccountDetailRoute } from '@/features/SalesforceAccounts/pages/SalesforceAccountDetailPage'
import { Button, Field, HStack, Input } from '@chakra-ui/react'
import { useNavigate } from '@tanstack/react-router'
import { type FormEvent, useState } from 'react'
import { useTranslation } from 'react-i18next'

/**
 * The lookup on `/salesforce-accounts`: one field, one button, straight to
 * the account's page.
 *
 * The typed value is validated and trimmed before navigating, so a malformed
 * id never spends a request to be told what the pattern already says and the
 * id in the URL is byte-for-byte the id that gets queried.
 *
 * Errors appear on submit, not on every keystroke: a 15-character id is
 * invalid for its first fourteen characters.
 */
export function AccountLookupForm() {
  const { t } = useTranslation('features', { useSuspense: false })
  const navigate = useNavigate()

  const [value, setValue] = useState('')
  const [error, setError] = useState<string | null>(null)

  const handleSubmit = (event: FormEvent) => {
    event.preventDefault()
    const trimmed = value.trim()

    if (trimmed.length === 0) {
      setError(String(t('salesforceAccounts.index.validation.required')))
      return
    }
    if (!isValidSalesforceAccountId(trimmed)) {
      setError(String(t('salesforceAccounts.index.validation.format')))
      return
    }

    setError(null)
    void navigate({
      to: salesforceAccountDetailRoute.to,
      params: { salesforceAccountId: trimmed },
    })
  }

  return (
    <form onSubmit={handleSubmit}>
      <Field.Root invalid={Boolean(error)} maxW="32rem">
        <Field.Label>
          {String(t('salesforceAccounts.index.form.label'))}
        </Field.Label>
        <HStack w="100%" align="start" gap={3}>
          <Input
            value={value}
            onChange={(event) => {
              setValue(event.target.value)
              // Cleared on edit, not re-validated: the message answers the
              // submit that produced it and stops applying the moment the
              // value changes.
              setError(null)
            }}
            placeholder={String(t('salesforceAccounts.index.form.placeholder'))}
            fontFamily="mono"
          />
          <Button type="submit" colorPalette="brand">
            {String(t('salesforceAccounts.index.form.submit'))}
          </Button>
        </HStack>
        {error ? (
          <Field.ErrorText>{error}</Field.ErrorText>
        ) : (
          <Field.HelperText>
            {String(t('salesforceAccounts.index.form.helper'))}
          </Field.HelperText>
        )}
      </Field.Root>
    </form>
  )
}
